import { useEffect, useState } from "react";
import { useOrganization } from "@clerk/clerk-react";
import { Member } from "./types";
import { inviteMembers } from "./service";
import { mixpanelCustomEvent } from "components/mixpanel/eventTriggers";
import { MixpanelEventName } from "components/mixpanel/types";

export const useMembers = () => {
  const user = JSON.parse(localStorage.getItem("user") || "{}");

  const mixpanelProps = {
    $name: `${user?.name}`,
    $email: user?.email,
  };

  const { organization, memberships, invitations } = useOrganization({
    memberships: true,
    invitations: true,
  });

  const [members, setMembers] = useState<Member[]>([]);
  const [invites, setInvites] = useState<any[]>([]);

  const isLoaded = Boolean(memberships?.data && invitations?.data);

  useEffect(() => {
    if (!memberships?.data) return;

    setMembers(
      memberships.data.map((m: any) => ({
        id: m.id,
        userId: m.publicUserData?.userId || "",
        name:
          `${m.publicUserData?.firstName ?? ""} ${
            m.publicUserData?.lastName ?? ""
          }`.trim() || "Unknown",
        email: m.publicUserData?.identifier ?? "",
        joinedAt: new Date(m.createdAt).toLocaleDateString(),
        role: m.roleName,
      }))
    );
  }, [memberships?.data]);

  useEffect(() => {
    if (!invitations?.data) return;
    setInvites(invitations.data);
  }, [invitations?.data]);

  const invite = async (emails: string[]) => {
    if (!organization) return;
    try {
      await inviteMembers(emails);
      invitations?.revalidate?.();
      mixpanelCustomEvent({
        mixpanelProps: {
          ...mixpanelProps,
          invitedEmails: emails.join(", "),
        },
        id: user?.uid?.toString(),
        eventName: MixpanelEventName.userInvitedMembersToTeam,
      });
    } catch (error: any) {
      throw error;
    }
  };

  const remove = async (memberId: string) => {
    try {
      if (!organization) return;

      await organization.removeMember(memberId);

      setTimeout(() => memberships?.revalidate?.(), 0);
      mixpanelCustomEvent({
        mixpanelProps: {
          ...mixpanelProps,
          removedMemberID: memberId,
        },
        id: user?.uid?.toString(),
        eventName: MixpanelEventName.userRemovedMemberFromTeam,
      });
    } catch {
      console.log("error", "Failed to remove member");
    }
  };

  const revokeInvitation = async () => {
    invitations?.revalidate?.();
    mixpanelCustomEvent({
      mixpanelProps,
      id: user?.uid?.toString(),
      eventName: MixpanelEventName.userRevokedInvitationFromMember,
    });
  };

  return {
    members,
    invitations: invites,
    invite,
    remove,
    revokeInvitation,
    alert,
    isLoaded,
  };
};
